import { Modal } from '@lmt/unicorn-shared/app/components/common';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { Button } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import {
  getAdminSettings,
  getSuperUserLoginForm,
  getSuperUserPasswordSetter,
} from '@lmt/unicorn-shared/app/components/DesktopUI';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { checkAndRefreshPermissions } from '@lmt/unicorn-shared/app/features/auth/permissions';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import { NetworkError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useMatomoPageTracking } from '@lmt/unicorn-shared/app/helpers/matomo';
import { default as i18next } from 'i18next';
import { getBridge } from 'lmt-patientmanager/utils/bridge';
import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Launcher.module.scss';
import {
  getAuthData,
  logOut,
} from '@lmt/unicorn-shared/app/features/auth/operations';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import { getSettings } from '@lmt/unicorn-shared/app/features/settings/selectors';

export const Launcher: FC<
  React.PropsWithChildren<{
    backendStartFailed: boolean;
  }>
> = (props) => {
  const [launched, setLaunched] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const isNightTheme = useUniSelector(getSettings).isNightTheme;

  const [retry, setRetry] = useState(0);

  useMatomoPageTracking();

  useEffect(() => {
    if (isNightTheme) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [isNightTheme]);

  useEffect(() => {
    void (async () => {
      try {
        const tokenData = getAuthData();
        if (!props.backendStartFailed && tokenData) {
          await checkAndRefreshPermissions(tokenData);
        }
        setLaunched(true);
      } catch (e) {
        console.error('Launcher.useEffect', e);
        setError(convertToErrorString(e));
      }
    })();
  }, [retry]);

  const SuperUserPasswordSetter = getSuperUserPasswordSetter();

  const [isSuperUserNeedSetting, setIsSuperUserNeedSetting] = useState(false);
  const [isSuperUserPasswordSetterOpen, setSuperUserPasswordSetterOpen] =
    useState(isSuperUserNeedSetting && !!SuperUserPasswordSetter);
  const [isSuperUserLoginOpen, setSuperUserLoginOpen] = useState(
    !isSuperUserNeedSetting && !isSuperUserPasswordSetterOpen,
  );
  const [isServerSettingsOpen, setServerSettingsOpen] = useState(
    !isSuperUserPasswordSetterOpen && !isSuperUserLoginOpen,
  );

  useEffect(() => {
    if (isElectron()) {
      const bridge = getBridge();
      bridge
        .getSuperUserSettings()
        .then((x) => {
          const superUserNeedSetting = !x.passwordHash && !x.password;
          const superUserPasswordSetterOpen =
            superUserNeedSetting && !!SuperUserPasswordSetter;
          const superUserLoginOpen =
            !superUserNeedSetting && !superUserPasswordSetterOpen;
          const serverSettingsOpen =
            !superUserPasswordSetterOpen && !superUserLoginOpen;

          setIsSuperUserNeedSetting(superUserNeedSetting);
          setSuperUserPasswordSetterOpen(superUserPasswordSetterOpen);
          setSuperUserLoginOpen(superUserLoginOpen);
          setServerSettingsOpen(serverSettingsOpen);
        })
        .catch((e) => console.error('Launcher.useEffect.isElectron', e));
    }
  }, []);

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div>
          {error === NetworkError
            ? i18next.t('translation:home.network_error')
            : error}
        </div>
        <div className={styles.errorContainerButtons}>
          <Button
            title={i18next.t('translation:home.retry_button')}
            onClick={() => {
              setError(undefined);
              setRetry(retry + 1);
            }}
          />{' '}
          or{' '}
          <Button
            title={i18next.t('translation:load.log_out')}
            onClick={() => {
              logOut();
              navigate(pages.home.link());
            }}
          />
        </div>
      </div>
    );
  }

  if (props.backendStartFailed) {
    const AdminSettings = getAdminSettings();
    const SuperUserLoginForm = getSuperUserLoginForm();
    return (
      <>
        {isSuperUserPasswordSetterOpen && (
          <Modal
            isOpen
            contentLabel={i18next.t('translation:home.super_user_settings')}
            onRequestClose={() => {}}
          >
            <SuperUserPasswordSetter
              onSuccess={() => {
                setSuperUserPasswordSetterOpen(false);
                setServerSettingsOpen(true);
              }}
            />
          </Modal>
        )}
        {isSuperUserLoginOpen && (
          <Modal
            isOpen
            contentLabel={i18next.t('translation:home.super_user_login')}
            onRequestClose={() => {}}
          >
            <SuperUserLoginForm
              onSuccess={() => {
                setSuperUserLoginOpen(false);
                setServerSettingsOpen(true);
              }}
            />
          </Modal>
        )}
        {isServerSettingsOpen && (
          <Modal
            contentLabel={i18next.t('translation:home.admin_settings')}
            isOpen
            onRequestClose={() => {
              /*nothing will be done*/
            }}
          >
            <AdminSettings backendStartFailed={true} />
          </Modal>
        )}
      </>
    );
  }

  return (
    <Loading
      loading={!launched}
      title={i18next.t('translation:load.loading', 'Loading...')}
    >
      {launched && (props.children as React.ReactElement<any>)}
    </Loading>
  );
};
